import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PLATFORM_ID, Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DataService, PamlabApiServiceService } from '@/shared/services';
import { isPlatformBrowser } from '@angular/common';
import { MatOption, MatSelectChange } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { RouteService } from '@/shared/services';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'cohort-form',
  templateUrl: './cohort-form.component.html',
  styleUrls: ['./cohort-form.component.css'],
  standalone: false,
})
export class CohortFormComponent implements OnInit {
  // variable name cohortGroup of type event emitter string
  @Output() cohortGroup = new EventEmitter<string>();

  public env = environment;

  //variable to keep track of spinner's status
  public spinnerBool: boolean = true;
  yearList = [2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024];

  // variable that contains tiers for form
  tierList = [1, 2, 3, 4, 5, 0]; // 0 means no tier
  admitTypes: string[] = ['FTIC', 'FCSAA', 'FCSAS', 'OtherAA', 'OtherAS'];

  // subscription for data
  private subscription: Subscription = new Subscription();
  private prevSelectedYears: any[] = [];
  private prevSelectedAdmitTypes: any[] = [];
  private prevSelectedTiers: any[] = [];

  // cohort form group
  public cohortForm = new FormGroup({
    years: new FormControl<any[]>([2023, 2024], [Validators.required]),
    admit_types: new FormControl<string[]>(['FTIC'], [Validators.required]),
    tiers: new FormControl<any[]>([], [Validators.required]),
    financial_aid: new FormControl('all', [Validators.required]),
    high_school_gpa_lower: new FormControl(0),
    high_school_gpa_upper: new FormControl(5),
    uwf_gpa_lower: new FormControl(0),
    uwf_gpa_upper: new FormControl(4),
  });
  @ViewChild('allTiersOption', { static: false }) allTiersOption!: MatOption;

  constructor(
    public dialog: MatDialog,
    private _apiService: PamlabApiServiceService,
    private _dataService: DataService,
    private _routeService: RouteService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  ngOnInit(): void {
    setTimeout(() => {
      this.allTiersOption.select();
      this.cohortFormSubmit();
    }, 500);
  }

  ngOnDestroy(): void {
    setTimeout(() => {
      this.subscription.unsubscribe();
    }, 3000);
  }

  isBrowser() {
    return isPlatformBrowser(this.platformId);
  }

  cohortFormSubmit() {
    this._dataService.setPendingData(true);

    const params = {
      years: this.cohortForm.value.years!.filter((x) => x !== 'All'),
      admit_types: this.cohortForm.value.admit_types!.filter(
        (x) => x !== 'All',
      ),
      tiers: this.cohortForm.value.tiers!.filter((x) => x !== 'All'),
      financial_aid: this.cohortForm.value.financial_aid!,
      high_school_gpa: [
        this.cohortForm.value.high_school_gpa_lower!,
        this.cohortForm.value.high_school_gpa_upper!,
      ],
      uwf_gpa: [
        this.cohortForm.value.uwf_gpa_lower!,
        this.cohortForm.value.uwf_gpa_upper!,
      ],
    };

    this.subscription.add(
      // calling _apiService to get killer courses based on form inputs.
      this._apiService.getCohort(params).subscribe({
        next: (data) => {
          this._dataService.setData({ data });
          this._dataService.setPendingData(false);
        },
        error: (errorRes: HttpErrorResponse) => {
          if (errorRes.status === 401) {
            this._routeService.navigate(environment['login_url']);
          }
          this._dataService.setData({ error: errorRes.error });
          this._dataService.setPendingData(false);
        },
      }),
    );
  }

  handleYearsUpdate(event: MatSelectChange) {
    const selectedValues = event.value as string[];
    const selectedValuesWithNoAll = selectedValues.filter((x) => x !== 'All');

    if (
      !this.prevSelectedYears.includes('All') &&
      selectedValues.includes('All')
    ) {
      this.cohortForm.patchValue({
        years: ['All', ...this.yearList.map((x) => x)],
      });
      this.prevSelectedYears = selectedValues;
      return;
    }

    if (
      this.prevSelectedYears.includes('All') &&
      !selectedValues.includes('All')
    ) {
      this.cohortForm.patchValue({ years: [] });
      this.prevSelectedYears = selectedValues;
      return;
    }

    if (
      (selectedValues.includes('All') &&
        selectedValuesWithNoAll.length === this.yearList.length) ||
      (!selectedValues.includes('All') &&
        selectedValuesWithNoAll.length === this.yearList.length)
    ) {
      this.cohortForm.patchValue({
        years: ['All', ...this.yearList.map((x) => x)],
      });
      this.prevSelectedYears = ['All', ...this.yearList.map((x) => x)];
      return;
    }

    const years = selectedValues.filter((x) => x !== 'All');
    this.cohortForm.patchValue({ years });
    this.prevSelectedYears = years;
  }

  handleAdmitTypesUpdate(event: MatSelectChange) {
    const selectedValues = event.value as string[];
    const selectedValuesWithNoAll = selectedValues.filter((x) => x !== 'All');

    if (
      !this.prevSelectedAdmitTypes.includes('All') &&
      selectedValues.includes('All')
    ) {
      this.cohortForm.patchValue({
        admit_types: ['All', ...this.admitTypes.map((x) => x)],
      });
      this.prevSelectedAdmitTypes = selectedValues;
      return;
    }

    if (
      this.prevSelectedAdmitTypes.includes('All') &&
      !selectedValues.includes('All')
    ) {
      this.cohortForm.patchValue({ admit_types: [] });
      this.prevSelectedAdmitTypes = selectedValues;
      return;
    }

    if (
      (selectedValues.includes('All') &&
        selectedValuesWithNoAll.length === this.admitTypes.length) ||
      (!selectedValues.includes('All') &&
        selectedValuesWithNoAll.length === this.admitTypes.length)
    ) {
      this.cohortForm.patchValue({
        admit_types: ['All', ...this.admitTypes.map((x) => x)],
      });
      this.prevSelectedAdmitTypes = ['All', ...this.admitTypes.map((x) => x)];
      return;
    }

    const admit_types = selectedValues.filter((x) => x !== 'All');
    this.cohortForm.patchValue({ admit_types });
    this.prevSelectedAdmitTypes = admit_types;
  }

  handleTiersUpdate(event: MatSelectChange) {
    const selectedValues = event.value as string[];
    const selectedValuesWithNoAll = selectedValues.filter((x) => x !== 'All');

    if (
      !this.prevSelectedTiers.includes('All') &&
      selectedValues.includes('All')
    ) {
      this.cohortForm.patchValue({
        tiers: ['All', ...this.tierList.map((x) => x)],
      });
      this.prevSelectedTiers = selectedValues;
      return;
    }

    if (
      this.prevSelectedTiers.includes('All') &&
      !selectedValues.includes('All')
    ) {
      this.cohortForm.patchValue({ tiers: [] });
      this.prevSelectedTiers = selectedValues;
      return;
    }

    if (
      (selectedValues.includes('All') &&
        selectedValuesWithNoAll.length === this.tierList.length) ||
      (!selectedValues.includes('All') &&
        selectedValuesWithNoAll.length === this.tierList.length)
    ) {
      this.cohortForm.patchValue({
        tiers: ['All', ...this.tierList.map((x) => x)],
      });
      this.prevSelectedTiers = ['All', ...this.tierList.map((x) => x)];
      return;
    }

    const tiers = selectedValues.filter((x) => x !== 'All');
    this.cohortForm.patchValue({ tiers });
    this.prevSelectedTiers = tiers;
  }

  // raise an event with value from the toggle button
  checkGroup(group: string) {
    this.cohortGroup.emit(group);
  }

  // close popup dialog after form is submitted
  closeDialog() {
    this.dialog.closeAll();
  }

  disabledSubmitButton() {
    return !this.cohortForm.valid;
  }

  formatTiersDisplay(tiers: number[] | undefined) {
    if (!tiers) return '';
    return tiers.map((x) => x || 'No Tier');
  }
}
